import { base64urlnopad } from '@scure/base';
import { UnwrapKeyError } from './crypto';
import { UTF8 } from './util';

// visible for testing
export class ConcatKDF {

  /**
   * KDF as defined in <a href="https://doi.org/10.6028/NIST.SP.800-56Ar2">NIST SP 800-56A Rev. 2 Section 5.8.1</a> using SHA-256
   * 
   * @param z A shared secret
   * @param keyDataLen Desired key length (in bytes)
   * @param otherInfo Optional context info binding the derived key to a key agreement (see e.g. RFC 7518, Section 4.6.2)
   * @returns key data
   */
  public static async kdf(z: Uint8Array, keyDataLen: number, otherInfo: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
    const hashLen = 32; // output length of SHA-256
    const reps = Math.ceil(keyDataLen / hashLen);
    if (reps >= 0xFFFFFFFF) {
      throw new Error('unsupported keyDataLen');
    }
    if (4 + z.byteLength + otherInfo.byteLength > 0xFFFFFFFF) {
      // technically max hash length for sha256 is 2^64-1 bits, but JS doesn't allow (safe) 64 bit numbers
      // it is safe to restrict this kdf to smaller input lengths
      throw new Error('unsupported input length');
    }
    const key = new Uint8Array(reps * hashLen);
    const tmp = new ArrayBuffer(4 + z.byteLength + otherInfo.byteLength);
    for (let i = 0; i < reps; i++) {
      new DataView(tmp, 0, 4).setUint32(0, i + 1, false);
      new Uint8Array(tmp).set(z, 4);
      new Uint8Array(tmp).set(otherInfo, 4 + z.byteLength);
      const digest = await crypto.subtle.digest('SHA-256', tmp);
      key.set(new Uint8Array(digest), i * hashLen);
    }
    return key.slice(0, keyDataLen);
  }

}

export type JWEHeader = {
  kid?: string,
  enc?: 'A256GCM' | 'A128GCM',
  alg?: 'ECDH-ES' | 'ECDH-ES+A256KW' | 'PBES2-HS512+A256KW' | 'A256KW',
  apu?: string,
  apv?: string,
  epk?: JsonWebKey,
  p2c?: number,
  p2s?: string,
  jku?: string,
  cty?: 'json',
  crit?: string[],
  [other: string]: undefined | string | number | boolean | object; // allow further properties
};

export type JsonJWE = {
  protected: string,
  recipients: PerRecipientProperties[]
  iv: string,
  ciphertext: string,
  tag: string
};

type PerRecipientProperties = {
  encrypted_key: string;
  header: JWEHeader;
};

export const ECDH_P384: EcKeyImportParams | EcKeyGenParams = {
  name: 'ECDH',
  namedCurve: 'P-384'
};

// #region Recipients

export abstract class Recipient {

  constructor(readonly kid: string) { };

  /**
   * Encryptes a CEK using the recipient-specific `alg`.
   * @param cek The CEK that is used to encrypt a JWE payload.
   * @param commonHeader The protected and unprotected header (not per-recipient)
   * @returns The encrypted CEK and per-recipient header parameters for the used `alg`.
   */
  abstract encrypt(cek: CryptoKey, commonHeader: JWEHeader): Promise<PerRecipientProperties>;

  /**
   * Decrypts the CEK using the recipient-specific `alg`.
   * @param header JOSE header applicable for this recipient
   * @param encryptedKey Encrypted CEK for this recipient
   * @returns A non-extractable CEK suitable for decryption with AES-GCM.
   * @throws {UnwrapKeyError} if decryption failed
   */
  abstract decrypt(header: JWEHeader, encryptedKey: string): Promise<CryptoKey>;

  /**
   * Create a recipient using `alg: ECDH-ES+A256KW`. Also supports `ECDH-ES` with direct key agreement for decryption
   * 
   * @param kid The key ID used to distinguish multiple recipients
   * @param recipientKey The recipients static public key for encryption or private key for decryption
   * @param apu Optional information about the creator
   * @param apv Optional information about the recipient
   * @returns A new recipient
   */
  public static ecdhEs(kid: string, recipientKey: CryptoKey, apu: Uint8Array = new Uint8Array(), apv: Uint8Array = new Uint8Array()): Recipient {
    if (recipientKey?.type === 'secret' || recipientKey?.algorithm.name !== 'ECDH') {
      throw new Error('Unsupported recipient key');
    }
    return new EcdhRecipient(kid, recipientKey, apu, apv);
  }

  /**
   * Create a recipient using `alg: PBES2-HS512+A256KW`.
   * 
   * @param kid The key ID used to distinguish multiple recipients
   * @param password The password to feed into the KDF
   * @param iterations The PBKDF2 iteration count within [{@link PBES2.MIN_ITERATION_COUNT}, {@link PBES2.MAX_ITERATION_COUNT}] (defaults to {@link PBES2.DEFAULT_ITERATION_COUNT}) - ignored and read from the header's `p2c` value during decryption
   * @returns A new recipient
   */
  public static pbes2(kid: string, password: string, iterations: number = PBES2.DEFAULT_ITERATION_COUNT): Recipient {
    return new Pbes2Recipient(kid, password, iterations);
  }

  /**
   * Create a recipient using `alg: A256KW`.
   * 
   * @param kid The key ID used to distinguish multiple recipients
   * @param wrappingKey The key used to wrap the CEK
   * @returns A new recipient
   */
  public static a256kw(kid: string, wrappingKey: CryptoKey): Recipient {
    if (wrappingKey?.type !== 'secret' || wrappingKey?.algorithm.name !== 'AES-KW') {
      throw new Error('Unsupported wrapping key');
    }
    return new A256kwRecipient(kid, wrappingKey);
  }

}

class EcdhRecipient extends Recipient {

  constructor(readonly kid: string, private readonly recipientKey: CryptoKey, private readonly apu: Uint8Array = new Uint8Array(), private readonly apv: Uint8Array = new Uint8Array()) {
    super(kid);
  }

  async encrypt(cek: CryptoKey, commonHeader: JWEHeader): Promise<PerRecipientProperties> {
    if (this.recipientKey.type !== 'public') {
      throw new Error('Recipient public key required.');
    }
    const ephemeralKey = await crypto.subtle.generateKey(ECDH_P384, false, ['deriveBits']);
    const header: JWEHeader = {
      ...commonHeader,
      kid: this.kid,
      alg: 'ECDH-ES+A256KW',
      epk: await crypto.subtle.exportKey('jwk', ephemeralKey.publicKey),
      apu: base64urlnopad.encode(this.apu),
      apv: base64urlnopad.encode(this.apv)
    };
    const wrappingKey = await ECDH_ES.deriveKey(this.recipientKey, ephemeralKey.privateKey, 384, 32, header, false, { name: 'AES-KW', length: 256 }, ['wrapKey']);
    const encryptedKey = new Uint8Array(await crypto.subtle.wrapKey('raw', cek, wrappingKey, 'AES-KW'));
    return {
      header: header,
      encrypted_key: base64urlnopad.encode(encryptedKey)
    };
  }

  async decrypt(header: JWEHeader, encryptedKey: string): Promise<CryptoKey> {
    if (this.recipientKey.type !== 'private') {
      throw new Error('Recipient private key required.');
    }
    if (header.alg === 'ECDH-ES' && header.epk) {
      return this.decryptDirect(header, { name: 'AES-GCM', length: 256 }, ['decrypt']);
    } else if (header.alg === 'ECDH-ES+A256KW' && header.epk) {
      return this.decryptAndUnwrap(header, encryptedKey);
    } else {
      throw new Error('Missing or invalid header parameters.');
    }
  }

  async decryptDirect(header: JWEHeader, keyAlgorithm: AesKeyAlgorithm, keyUsage: KeyUsage[]): Promise<CryptoKey> {
    let keyBits: number;
    switch (header.epk!.crv) {
      case 'P-256':
        keyBits = 256;
        break;
      case 'P-384':
        keyBits = 384;
        break;
      default:
        throw new Error('Unsupported curve');
    }
    const epk = await crypto.subtle.importKey('jwk', header.epk!, { name: 'ECDH', namedCurve: header.epk?.crv }, false, []);
    return ECDH_ES.deriveKey(epk, this.recipientKey, keyBits, keyAlgorithm.length / 8, header, false, keyAlgorithm, keyUsage);
  }

  async decryptAndUnwrap(header: JWEHeader, encryptedKey: string): Promise<CryptoKey> {
    const wrappingKey = await this.decryptDirect(header, { name: 'AES-KW', length: 256 }, ['unwrapKey']);
    try {
      return await crypto.subtle.unwrapKey('raw', base64urlnopad.decode(encryptedKey) as Uint8Array<ArrayBuffer>, wrappingKey, 'AES-KW', { name: 'AES-GCM' }, false, ['decrypt']);
    } catch (error) {
      throw new UnwrapKeyError(error);
    }
  }

}

class A256kwRecipient extends Recipient {

  constructor(readonly kid: string, private readonly wrappingKey: CryptoKey) {
    super(kid);
  }

  async encrypt(cek: CryptoKey, commonHeader: JWEHeader): Promise<PerRecipientProperties> {
    const header: JWEHeader = {
      ...commonHeader,
      kid: this.kid,
      alg: 'A256KW'
    };
    const encryptedKey = new Uint8Array(await crypto.subtle.wrapKey('raw', cek, this.wrappingKey, 'AES-KW'));
    return {
      header: header,
      encrypted_key: base64urlnopad.encode(encryptedKey)
    };
  }

  async decrypt(header: JWEHeader, encryptedKey: string): Promise<CryptoKey> {
    if (header.alg != 'A256KW') {
      throw new Error('unsupported alg');
    }
    try {
      return await crypto.subtle.unwrapKey('raw', base64urlnopad.decode(encryptedKey) as Uint8Array<ArrayBuffer>, this.wrappingKey, 'AES-KW', { name: 'AES-GCM' }, false, ['decrypt']);
    } catch (error) {
      throw new UnwrapKeyError(error);
    }
  }

}

class Pbes2Recipient extends Recipient {

  constructor(readonly kid: string, private readonly password: string, private readonly iterations: number) {
    super(kid);
    PBES2.validateIterationCount(iterations); // fail at encryption time already, otherwise we'd produce a JWE that decrypt() rejects
  }

  async encrypt(cek: CryptoKey, commonHeader: JWEHeader): Promise<PerRecipientProperties> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const header: JWEHeader = {
      ...commonHeader,
      kid: this.kid,
      alg: 'PBES2-HS512+A256KW',
      p2c: this.iterations,
      p2s: base64urlnopad.encode(salt)
    };
    const wrappingKey = PBES2.deriveWrappingKey(this.password, 'PBES2-HS512+A256KW', salt, this.iterations);
    const encryptedKey = new Uint8Array(await crypto.subtle.wrapKey('raw', cek, await wrappingKey, 'AES-KW'));
    return {
      header: header,
      encrypted_key: base64urlnopad.encode(encryptedKey)
    };
  }

  async decrypt(header: JWEHeader, encryptedKey: string): Promise<CryptoKey> {
    if (header.alg != 'PBES2-HS512+A256KW' || !header.p2s || !header.p2c) {
      throw new Error('Missing or invalid header parameters.');
    }
    PBES2.validateIterationCount(header.p2c); // p2c is unauthenticated at this point, clamp it before running the KDF
    const salt = base64urlnopad.decode(header.p2s);
    const wrappingKey = await PBES2.deriveWrappingKey(this.password, 'PBES2-HS512+A256KW', salt, header.p2c);
    try {
      return await crypto.subtle.unwrapKey('raw', base64urlnopad.decode(encryptedKey) as Uint8Array<ArrayBuffer>, wrappingKey, 'AES-KW', { name: 'AES-GCM' }, false, ['decrypt']);
    } catch (error) {
      throw new UnwrapKeyError(error);
    }
  }

}

// #endregion
// #region JWE

export class JWE {

  private constructor(private readonly payload: object, private readonly protectedHeader: JWEHeader) { }

  public static build(payload: object, protectedHeader: JWEHeader = {}): JWE {
    return new JWE(payload, protectedHeader);
  }

  public static parseCompact(token: string): EncryptedJWE {
    const [protectedHeader, encryptedKey, iv, ciphertext, tag] = token.split('.', 5);
    // compact serialization has no per-recipient header, all parameters are in the protected header
    return new EncryptedJWE(protectedHeader, [{ encrypted_key: encryptedKey, header: {} }], iv, ciphertext, tag);
  }

  public static parseJson(jwe: JsonJWE): EncryptedJWE {
    if (!jwe.protected || !jwe.recipients || !jwe.iv || !jwe.ciphertext || !jwe.tag) {
      throw new Error('Malformed JWE');
    }
    return new EncryptedJWE(jwe.protected, jwe.recipients, jwe.iv, jwe.ciphertext, jwe.tag);
  }

  /**
   * Specifies the recipients of this JWE. Encryption is deferred until a serialization is requested.
   * @param recipient first recipient
   * @param moreRecipients further recipients
   * @returns a JWE that can be serialized via {@link PendingJWE#toCompact} or {@link PendingJWE#toJson}
   */
  public withRecipients(recipient: Recipient, ...moreRecipients: Recipient[]): PendingJWE {
    return new PendingJWE(this.payload, this.protectedHeader, [recipient, ...moreRecipients]);
  }

}

/**
 * A JWE with known recipients, waiting for a serialization to be chosen.
 * The serialization determines the header layout (which is authenticated), so encryption only happens in the terminal call.
 */
export class PendingJWE {

  private consumed = false;

  constructor(private readonly payload: object, private readonly protectedHeader: JWEHeader, private readonly recipients: Recipient[]) { }

  /**
   * Encrypts and serializes this JWE in Compact Serialization (RFC 7516, Section 7.1), moving all per-recipient header parameters into the protected header.
   * @returns the compact JWE
   */
  public async toCompact(): Promise<string> {
    if (this.recipients.length !== 1) {
      throw new Error('JWE Compact Serialization requires exactly one recipient.');
    }
    return (await this.encrypt(true)).toCompact();
  }

  /**
   * Encrypts and serializes this JWE in JSON Serialization (RFC 7516, Section 7.2), keeping per-recipient header parameters per recipient.
   * @returns the JSON JWE
   */
  public async toJson(): Promise<JsonJWE> {
    return (await this.encrypt(false)).toJson();
  }

  private async encrypt(foldIntoProtectedHeader: boolean): Promise<EncryptedJWE> {
    if (this.consumed) {
      throw new Error('JWE has already been encrypted.');
    }
    this.consumed = true;
    let protectedHeader: JWEHeader = {
      ...this.protectedHeader,
      enc: 'A256GCM'
    };
    const cek = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const perRecipientData = await Promise.all(this.recipients.map(r => r.encrypt(cek, protectedHeader)));

    if (foldIntoProtectedHeader) {
      protectedHeader = {
        ...protectedHeader,
        ...perRecipientData[0].header
      };
    }
    // header parameter names in the protected and per-recipient headers must be disjoint (RFC 7516, Section 2):
    for (const key of Object.keys(protectedHeader)) {
      perRecipientData.forEach(r => delete r.header[key]);
    }

    const encodedProtectedHeader = base64urlnopad.encode(UTF8.encode(JSON.stringify(protectedHeader)));
    const m = UTF8.encode(JSON.stringify(this.payload));
    const ciphertextAndTag = new Uint8Array(await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        additionalData: UTF8.encode(encodedProtectedHeader),
        tagLength: 128
      },
      cek,
      m
    ));
    console.assert(ciphertextAndTag.byteLength > 16, 'result of GCM encryption expected to contain 128bit tag');
    const ciphertext = ciphertextAndTag.slice(0, ciphertextAndTag.byteLength - 16);
    const tag = ciphertextAndTag.slice(ciphertextAndTag.byteLength - 16);

    const encodedIv = base64urlnopad.encode(iv);
    const encodedCiphertext = base64urlnopad.encode(ciphertext);
    const encodedTag = base64urlnopad.encode(tag);
    return new EncryptedJWE(encodedProtectedHeader, perRecipientData, encodedIv, encodedCiphertext, encodedTag);
  }

}

// visible for testing
export class EncryptedJWE {

  constructor(private readonly protectedHeader: string, private readonly perRecipient: PerRecipientProperties[], private readonly iv: string, private readonly ciphertext: string, private readonly tag: string) {
    if (perRecipient.length < 1) {
      throw new Error('Expected at least one recipient.');
    }
  }

  public toJson(): JsonJWE {
    if (this.perRecipient.length < 1) {
      throw new Error('JWE JSON Serialization requires at least one recipient.');
    }
    const recipients = this.perRecipient.map(r => ({
      header: r.header,
      encrypted_key: r.encrypted_key
    }));
    return {
      protected: this.protectedHeader,
      recipients: recipients,
      iv: this.iv,
      ciphertext: this.ciphertext,
      tag: this.tag
    };
  }

  public toCompact(): string {
    if (this.perRecipient.length !== 1) {
      throw new Error('JWE Compact Serialization requires exactly one recipient.');
    }
    return `${this.protectedHeader}.${this.perRecipient[0].encrypted_key}.${this.iv}.${this.ciphertext}.${this.tag}`;
  }

  /**
   * The decoded protected header of this JWE.
   * @returns protected header parameters
   */
  public get header(): JWEHeader {
    return JSON.parse(UTF8.decode(base64urlnopad.decode(this.protectedHeader)));
  }

  /**
   * Decrypts the JWE for the given recipient.
   * @param recipient the recipient whose key is used to decrypt the CEK
   * @param understoodCriticalParams names of the extension header parameters this caller processes; the JWE is rejected if its `crit` header lists any other parameter (RFC 7516, Section 4.1.13)
   * @returns the decrypted JSON payload
   * @throws Error if the JWE is malformed, uses unsupported critical header parameters or can not be decrypted
   */
  public async decrypt(recipient: Recipient, understoodCriticalParams: string[] = []): Promise<any> {
    const protectedHeader = this.header;
    const perRecipientData = (this.perRecipient.length === 1)
      ? this.perRecipient[0]
      : this.perRecipientWithKid(recipient.kid);
    EncryptedJWE.checkCriticalParams(protectedHeader, perRecipientData.header, understoodCriticalParams);
    const combinedHeader: JWEHeader = { ...perRecipientData.header, ...protectedHeader };
    const cek = await recipient.decrypt(combinedHeader, perRecipientData.encrypted_key);
    const ciphertext = base64urlnopad.decode(this.ciphertext);
    const tag = base64urlnopad.decode(this.tag);
    const ciphertextAndTag = new Uint8Array([...ciphertext, ...tag]);
    const cleartext = new Uint8Array(await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: base64urlnopad.decode(this.iv) as Uint8Array<ArrayBuffer>,
        additionalData: UTF8.encode(this.protectedHeader),
        tagLength: 128
      },
      cek,
      ciphertextAndTag
    ));
    return JSON.parse(UTF8.decode(cleartext));
  }

  // header parameters registered by RFC 7516 (Section 4.1) and RFC 7518 (Sections 4.6.1, 4.7.1, 4.8.1), which MUST NOT be listed in `crit`
  private static readonly REGISTERED_HEADER_PARAMS: ReadonlySet<string> = new Set(['alg', 'enc', 'zip', 'jku', 'jwk', 'kid', 'x5u', 'x5c', 'x5t', 'x5t#S256', 'typ', 'cty', 'crit', 'epk', 'apu', 'apv', 'iv', 'tag', 'p2s', 'p2c']);

  // enforces the `crit` header parameter as defined in RFC 7516, Section 4.1.13 (which refers to RFC 7515, Section 4.1.11)
  private static checkCriticalParams(protectedHeader: JWEHeader, perRecipientHeader: JWEHeader, understoodCriticalParams: string[]): void {
    if ('crit' in perRecipientHeader) {
      throw new Error('Header parameter "crit" must be integrity protected');
    }
    if (!('crit' in protectedHeader)) {
      return;
    }
    const crit = protectedHeader.crit;
    if (!Array.isArray(crit) || crit.length === 0 || !crit.every(name => typeof name === 'string')) {
      throw new Error('Header parameter "crit" must be a non-empty array of strings');
    }
    for (const name of crit) {
      if (EncryptedJWE.REGISTERED_HEADER_PARAMS.has(name)) {
        throw new Error(`Header parameter "crit" must not list registered header parameter: ${name}`);
      }
      if (!(name in protectedHeader)) {
        throw new Error(`Critical header parameter is missing: ${name}`);
      }
      if (!understoodCriticalParams.includes(name)) {
        throw new Error(`Unsupported critical header parameter: ${name}`);
      }
    }
  }

  private perRecipientWithKid(kid: string): PerRecipientProperties {
    const result = this.perRecipient.find(r => r.header.kid === kid);
    if (result) {
      return result;
    } else {
      throw new Error(`JWE does not contain recipient with kid: ${kid}`);
    }
  }

}

// #endregion
// #region Utilities

// visible for testing
export class ECDH_ES {

  private static async deriveRawKey(publicKey: CryptoKey, privateKey: CryptoKey, ecdhKeyBits: number, desiredKeyBytes: number, header: JWEHeader): Promise<Uint8Array<ArrayBuffer>> {
    let agreedKey = new Uint8Array();
    try {
      const algOrEnc = header.alg === 'ECDH-ES' ? header.enc : header.alg; // see definition of AlgorithmID in RFC 7518, Section 4.6.2
      if (!algOrEnc) {
        throw new Error('Missing alg or enc header parameter');
      }
      const algorithmId = ECDH_ES.lengthPrefixed(UTF8.encode(algOrEnc));
      const partyUInfo = ECDH_ES.lengthPrefixed(base64urlnopad.decode(header.apu ?? ''));
      const partyVInfo = ECDH_ES.lengthPrefixed(base64urlnopad.decode(header.apv ?? ''));
      const suppPubInfo = new ArrayBuffer(4);
      const suppPrivInfo = new Uint8Array();
      new DataView(suppPubInfo).setUint32(0, desiredKeyBytes * 8, false);
      agreedKey = new Uint8Array(await crypto.subtle.deriveBits(
        {
          name: 'ECDH',
          public: publicKey
        },
        privateKey,
        ecdhKeyBits
      ));
      const otherInfo = new Uint8Array([...algorithmId, ...partyUInfo, ...partyVInfo, ...new Uint8Array(suppPubInfo), ...suppPrivInfo]);
      return ConcatKDF.kdf(new Uint8Array(agreedKey), desiredKeyBytes, otherInfo);
    } finally {
      agreedKey.fill(0x00);
    }
  }

  public static async deriveKey(publicKey: CryptoKey, privateKey: CryptoKey, ecdhKeyBits: number, desiredKeyBytes: number, header: JWEHeader, exportable: boolean, algorithm: AesKeyAlgorithm, usage: KeyUsage[]): Promise<CryptoKey> {
    let derivedKey = new Uint8Array();
    try {
      derivedKey = await this.deriveRawKey(publicKey, privateKey, ecdhKeyBits, desiredKeyBytes, header);
      return crypto.subtle.importKey('raw', derivedKey, algorithm, exportable, usage);
    } finally {
      derivedKey.fill(0x00);
    }
  }

  public static async deriveContentKey(publicKey: CryptoKey, privateKey: CryptoKey, ecdhKeyBits: number, desiredKeyBytes: number, header: JWEHeader, exportable: boolean = false): Promise<CryptoKey> {
    return this.deriveKey(publicKey, privateKey, ecdhKeyBits, desiredKeyBytes, header, exportable, { name: 'AES-GCM', length: desiredKeyBytes * 8 }, ['encrypt', 'decrypt']);
  }

  public static async deriveWrappingKey(publicKey: CryptoKey, privateKey: CryptoKey, ecdhKeyBits: number, desiredKeyBytes: number, header: JWEHeader, exportable: boolean = false): Promise<CryptoKey> {
    return this.deriveKey(publicKey, privateKey, ecdhKeyBits, desiredKeyBytes, header, exportable, { name: 'AES-KW', length: desiredKeyBytes * 8 }, ['wrapKey', 'unwrapKey']);
  }

  public static lengthPrefixed(data: Uint8Array): Uint8Array {
    const result = new Uint8Array(4 + data.byteLength);
    new DataView(result.buffer, 0, 4).setUint32(0, data.byteLength, false);
    result.set(data, 4);
    return result;
  }

}

// visible for testing
export class PBES2 {

  public static readonly DEFAULT_ITERATION_COUNT = 1000000;
  public static readonly MIN_ITERATION_COUNT = 1000; // minimum recommended by RFC 7518, Section 4.8.1.2
  public static readonly MAX_ITERATION_COUNT = 16777216; // 2^24, upper sanity bound to prevent resource exhaustion via an attacker-controlled p2c header
  private static readonly NULL_BYTE = Uint8Array.of(0x00);

  /**
   * Checks that a PBKDF2 iteration count is an integer within [{@link PBES2.MIN_ITERATION_COUNT}, {@link PBES2.MAX_ITERATION_COUNT}].
   * @param iterations The iteration count to validate
   * @throws Error if the iteration count is unsupported
   */
  public static validateIterationCount(iterations: number): void {
    if (!Number.isInteger(iterations) || iterations < PBES2.MIN_ITERATION_COUNT || iterations > PBES2.MAX_ITERATION_COUNT) {
      throw new Error(`Unsupported PBKDF2 iteration count: ${iterations}`);
    }
  }

  public static async deriveWrappingKey(password: string, alg: 'PBES2-HS512+A256KW' | 'PBES2-HS256+A128KW', salt: Uint8Array, iterations: number, extractable: boolean = false): Promise<CryptoKey> {
    let hash, keyLen;
    if (alg == 'PBES2-HS512+A256KW') {
      hash = 'SHA-512';
      keyLen = 256;
    } else if (alg == 'PBES2-HS256+A128KW') {
      hash = 'SHA-256';
      keyLen = 128;
    } else {
      throw new Error('only PBES2-HS512+A256KW and PBES2-HS256+A128KW supported');
    }
    const encodedPw = UTF8.encode(password);
    const pwKey = crypto.subtle.importKey(
      'raw',
      encodedPw,
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        hash: hash,
        salt: new Uint8Array([...UTF8.encode(alg), ...PBES2.NULL_BYTE, ...salt]), // see https://www.rfc-editor.org/rfc/rfc7518#section-4.8.1.1
        iterations: iterations
      },
      await pwKey,
      {
        name: 'AES-KW',
        length: keyLen
      },
      extractable,
      ['wrapKey', 'unwrapKey']
    );
  }

}

// #endregion
